// DOCX Import / Export for TipTap editor.
//
// Import: mammoth.js converts .docx → HTML → TipTap setContent
// Export: prosemirror-docx serializes ProseMirror doc → .docx via docx package

import type { Editor } from '@tiptap/react';
import mammoth from 'mammoth/mammoth.browser';
import { DocxSerializer, defaultNodes, defaultMarks } from 'prosemirror-docx';
import {
  Packer,
  AlignmentType,
  Header,
  Footer,
  Paragraph,
  TextRun,
  PageNumber,
  NumberFormat,
  type IPropertiesOptions,
} from 'docx';
import { saveAs } from 'file-saver';

// ---------------------------------------------------------------------------
// Import: DOCX → TipTap
// ---------------------------------------------------------------------------

export interface ImportResult {
  /** Whether import was successful */
  ok: boolean;
  /** Warning messages from mammoth (e.g., unsupported features) */
  warnings: string[];
  /** Error message if import failed */
  error?: string;
}

/**
 * Import a .docx file into the TipTap editor.
 * Converts DOCX → HTML via mammoth, then sets editor content.
 */
export async function importDocx(file: File, editor: Editor): Promise<ImportResult> {
  try {
    const arrayBuffer = await file.arrayBuffer();

    const result = await mammoth.convertToHtml(
      { arrayBuffer },
      {
        // Custom style mappings for better fidelity
        styleMap: [
          "p[style-name='Quote'] => blockquote",
          "p[style-name='Intense Quote'] => blockquote",
          "p[style-name='Code'] => pre > code",
          "r[style-name='Code Char'] => code",
          "p[style-name='List Bullet'] => ul > li:fresh",
          "p[style-name='List Number'] => ol > li:fresh",
        ],
      },
    );

    const html = result.value;
    const warnings = result.messages
      .filter((m: any) => m.type === 'warning')
      .map((m: any) => m.message);

    // Set content in the editor
    editor.commands.setContent(html);

    return { ok: true, warnings };
  } catch (err) {
    return {
      ok: false,
      warnings: [],
      error: err instanceof Error ? err.message : 'Unknown import error',
    };
  }
}

// ---------------------------------------------------------------------------
// Export: TipTap → DOCX
// ---------------------------------------------------------------------------

export interface ExportOptions {
  /** Document title (used in DOCX metadata and header) */
  title?: string;
  /** Page size in mm */
  pageWidth?: number;
  pageHeight?: number;
  /** Margins in mm */
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  /** Header text (left, center, right) */
  headerText?: { left?: string; center?: string; right?: string };
  /** Footer text (left, center, right) */
  footerText?: { left?: string; center?: string; right?: string };
  /** Include page numbers in footer */
  pageNumbers?: boolean;
}

// Extend default serializer with text-alignment support
const customNodes = {
  ...defaultNodes,
  // Override paragraph to handle text alignment
  paragraph(state: any, node: any) {
    const alignment = node.attrs?.textAlign;
    const alignMap: Record<string, string> = {
      left: AlignmentType.LEFT,
      center: AlignmentType.CENTER,
      right: AlignmentType.RIGHT,
      justify: AlignmentType.JUSTIFIED,
    };
    if (alignment && alignMap[alignment]) {
      state.renderInline(node, { alignment: alignMap[alignment] });
    } else {
      state.renderInline(node);
    }
    state.closeBlock(node);
  },
  // Override heading to preserve alignment
  heading(state: any, node: any) {
    const alignment = node.attrs?.textAlign;
    const alignMap: Record<string, string> = {
      left: AlignmentType.LEFT,
      center: AlignmentType.CENTER,
      right: AlignmentType.RIGHT,
      justify: AlignmentType.JUSTIFIED,
    };
    const opts: any = {
      heading: `Heading${node.attrs.level}`,
    };
    if (alignment && alignMap[alignment]) {
      opts.alignment = alignMap[alignment];
    }
    state.renderInline(node, opts);
    state.closeBlock(node);
  },
};

// Extend marks with text color and highlight
const customMarks = {
  ...defaultMarks,
  textStyle(_state: any, _node: any, mark: any) {
    // Handle text color from TipTap's TextStyle + Color extension
    const color = mark.attrs?.color;
    if (color) {
      // Strip '#' prefix for docx color format
      return { color: color.replace('#', '') };
    }
    return {};
  },
  highlight(_state: any, _node: any, mark: any) {
    const color = mark.attrs?.color;
    if (color) {
      // DOCX highlight uses predefined colors; use shading for custom colors
      return { shading: { fill: color.replace('#', '') } };
    }
    return {};
  },
};

const serializer = new DocxSerializer(customNodes, customMarks);

/**
 * Export the TipTap editor content as a .docx file.
 * Downloads the file directly to the user's browser.
 */
export async function exportDocx(editor: Editor, options: ExportOptions = {}): Promise<void> {
  const {
    title = 'Document',
    pageWidth = 210,
    pageHeight = 297,
    marginTop = 20,
    marginBottom = 20,
    marginLeft = 30,
    marginRight = 15,
    headerText,
    footerText,
    pageNumbers = true,
  } = options;

  // Convert mm to twips (1mm = 56.7 twips)
  const mmToTwip = (mm: number) => Math.round(mm * 56.7);

  // Build header
  let header: Header | undefined;
  if (headerText) {
    const children: Paragraph[] = [];
    const runs: TextRun[] = [];
    if (headerText.left) runs.push(new TextRun({ text: headerText.left, size: 16 }));
    if (headerText.center) runs.push(new TextRun({ text: `\t${headerText.center}`, size: 16 }));
    if (headerText.right) runs.push(new TextRun({ text: `\t${headerText.right}`, size: 16 }));
    if (runs.length) children.push(new Paragraph({ children: runs, alignment: AlignmentType.LEFT }));
    header = new Header({ children });
  }

  // Build footer
  let footer: Footer | undefined;
  if (footerText || pageNumbers) {
    const children: Paragraph[] = [];
    const runs: TextRun[] = [];
    if (footerText?.left) runs.push(new TextRun({ text: footerText.left, size: 16 }));
    if (pageNumbers) {
      runs.push(new TextRun({ text: '\t', size: 16 }));
      runs.push(new TextRun({ children: [PageNumber.CURRENT], size: 16 }));
    }
    if (footerText?.right) runs.push(new TextRun({ text: `\t${footerText.right}`, size: 16 }));
    if (runs.length) children.push(new Paragraph({ children: runs }));
    footer = new Footer({ children });
  }

  // Serialize ProseMirror document to docx
  const wordDoc = serializer.serialize(
    editor.state.doc,
    {
      // Images are referenced by URL in our documents; nothing to inline.
      getImageBuffer: () => new Uint8Array(),
      sections: [
        {
          properties: {
            page: {
              size: {
                width: mmToTwip(pageWidth),
                height: mmToTwip(pageHeight),
              },
              margin: {
                top: mmToTwip(marginTop),
                bottom: mmToTwip(marginBottom),
                left: mmToTwip(marginLeft),
                right: mmToTwip(marginRight),
              },
              pageNumbers: { start: 1, formatType: NumberFormat.DECIMAL },
            },
          },
          headers: header ? { default: header } : undefined,
          footers: footer ? { default: footer } : undefined,
        },
      ],
    },
    () => ({ title, description: title } as IPropertiesOptions),
  );

  // Generate and download
  const blob = await Packer.toBlob(wordDoc);
  saveAs(blob, `${title.replace(/[^a-zA-Zа-яА-Я0-9_-]/g, '_')}.docx`);
}
